import { GameData, GameMode, HistoryItem, QuestionCategory, QuestionInstance, QuestionTemplate } from '../types';
import { getGameData } from './dataLoader';
import { getRandomTemplate } from './templates';
import { checkAnswer } from './templatesHelper';

type QuestionConstraints = {
  minPossibleAnswers?: number;
  maxPossibleAnswers?: number;
  minAvailableAnswers?: number;
};

type QuestionFilter = {
  mode?: GameMode;
  includeCategories?: QuestionCategory[];
  excludeCategories?: QuestionCategory[];
  includeTags?: string[];
  excludeTags?: string[];
};

const getSubjectIso3 = (params: any) =>
  (params && typeof params === 'object' && 'iso3' in params) ? params.iso3 : params;

export const getQuestionKey = (templateId: string, subjectIso3: string | null) =>
  `${templateId}::${subjectIso3 ?? 'none'}`;

export const buildRecentQuestionKeys = (history: HistoryItem[], limit: number): Set<string> => {
  const recent = history.slice(0, limit);
  return new Set(recent.map((item) => getQuestionKey(item.templateId, item.subjectIso3)));
};

export const countAvailableAnswers = (
  templateId: string,
  subjectIso3: string | null,
  data: GameData,
  usedIsoCodes: Set<string>,
  burnedIsoCodes: Set<string>
): number => data.countries.filter(country =>
  !usedIsoCodes.has(country.iso3) &&
  !burnedIsoCodes.has(country.iso3) &&
  checkAnswer(templateId, subjectIso3, country, data)
).length;

export const countPossibleAnswers = (
  templateId: string,
  subjectIso3: string | null,
  data: GameData
): number =>
  data.countries.filter(country =>
    checkAnswer(templateId, subjectIso3, country, data)
  ).length;

export const getRemainingCountryCount = (
  data: GameData,
  usedIsoCodes: Set<string>,
  burnedIsoCodes: Set<string>
): number => data.countries.length - usedIsoCodes.size - burnedIsoCodes.size;

const getProgressRatio = (
  data: GameData,
  usedIsoCodes: Set<string>,
  burnedIsoCodes: Set<string>
): number => {
  const remaining = getRemainingCountryCount(data, usedIsoCodes, burnedIsoCodes);
  if (data.countries.length === 0) return 0;
  return Math.min(1, Math.max(0, 1 - remaining / data.countries.length));
};

const getBaseMinEap = (progressRatio: number): number => {
  if (progressRatio < 0.2) return 25;
  if (progressRatio < 0.4) return 15;
  if (progressRatio < 0.6) return 10;
  if (progressRatio < 0.8) return 6;
  if (progressRatio < 0.9) return 3;
  return 2;
};

const isEndgame = (
  data: GameData,
  usedIsoCodes: Set<string>,
  burnedIsoCodes: Set<string>
): boolean => {
  const remaining = getRemainingCountryCount(data, usedIsoCodes, burnedIsoCodes);
  const progress = getProgressRatio(data, usedIsoCodes, burnedIsoCodes);
  return remaining <= 10 || progress >= 0.9;
};

const adjustMinEapForPerformance = (minEap: number, history: HistoryItem[]): number => {
  if (history.length === 0) return minEap;
  const recent = history.slice(0, 5);
  const incorrect = recent.filter((item) => !item.isCorrect).length;
  const correct = recent.filter((item) => item.isCorrect).length;
  let adjusted = minEap;
  if (incorrect >= 2) {
    adjusted += 3;
  }
  if (correct >= 5 && incorrect === 0) {
    adjusted = Math.max(1, adjusted - 2);
  }
  return adjusted;
};

const getLastQuestionEap = (
  history: HistoryItem[],
  data: GameData,
  usedIsoCodes: Set<string>,
  burnedIsoCodes: Set<string>
): number | null => {
  if (history.length === 0) return null;
  const last = history[0];
  return countAvailableAnswers(
    last.templateId,
    last.subjectIso3,
    data,
    usedIsoCodes,
    burnedIsoCodes
  );
};

const getMinEap = (
  data: GameData,
  history: HistoryItem[],
  usedIsoCodes: Set<string>,
  burnedIsoCodes: Set<string>
): number => {
  const baseMinEap = getBaseMinEap(getProgressRatio(data, usedIsoCodes, burnedIsoCodes));
  const performanceAdjusted = adjustMinEapForPerformance(baseMinEap, history);
  const lastQuestionEap = getLastQuestionEap(history, data, usedIsoCodes, burnedIsoCodes);
  const remaining = getRemainingCountryCount(data, usedIsoCodes, burnedIsoCodes);
  let minEap = performanceAdjusted;
  if (lastQuestionEap !== null && lastQuestionEap <= minEap + 1) {
    minEap += 3;
  }
  if (!isEndgame(data, usedIsoCodes, burnedIsoCodes)) {
    minEap = Math.max(minEap, 3);
  }
  return Math.min(minEap, remaining, data.countries.length);
};

const weightedRandomIndex = (weights: number[]): number => {
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  if (total <= 0) return Math.floor(Math.random() * weights.length);
  let roll = Math.random() * total;
  for (let i = 0; i < weights.length; i += 1) {
    roll -= weights[i];
    if (roll <= 0) return i;
  }
  return weights.length - 1;
};

const isWithinConstraints = (
  possibleCount: number,
  availableCount: number,
  constraints?: QuestionConstraints
): boolean => {
  if (!constraints) return true;
  if (constraints.maxPossibleAnswers !== undefined && possibleCount > constraints.maxPossibleAnswers) {
    return false;
  }
  if (constraints.minPossibleAnswers !== undefined && possibleCount < constraints.minPossibleAnswers) {
    return false;
  }
  if (constraints.minAvailableAnswers !== undefined && availableCount < constraints.minAvailableAnswers) {
    return false;
  }
  return true;
};

const hasTemplateReachedLimit = (template: QuestionTemplate, history: HistoryItem[]): boolean => {
  if (template.maxOccurrencesPerGame === undefined) return false;
  const usedCount = history.filter((item) => item.templateId === template.id).length;
  return usedCount >= template.maxOccurrencesPerGame;
};

export const generateQuestion = (
  currentCount: number,
  lastCategory: QuestionTemplate['category'] | null,
  usedIsoCodes: Set<string>,
  burnedIsoCodes: Set<string>,
  recentQuestionKeys: Set<string>,
  history: HistoryItem[],
  constraints?: QuestionConstraints,
  filters?: QuestionFilter
): QuestionInstance | null => {
  const data = getGameData();
  const maxAttempts = 200;

  const attemptGenerate = (allowRecent: boolean): QuestionInstance | null => {
    const candidateMap = new Map<string, {
      template: QuestionTemplate;
      params: any;
      subjectIso3: string | null;
      availableCount: number;
      questionKey: string;
      possibleCount: number;
    }>();

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const category = attempt > maxAttempts / 2 ? null : lastCategory;
      const { template, params } = getRandomTemplate(category, data, filters);
      const subjectIso3 = getSubjectIso3(params);
      const questionKey = getQuestionKey(template.id, subjectIso3);
      if (!allowRecent && recentQuestionKeys.has(questionKey)) {
        continue;
      }
      if (hasTemplateReachedLimit(template, history)) {
        continue;
      }

      const availableCount = countAvailableAnswers(
        template.id,
        subjectIso3,
        data,
        usedIsoCodes,
        burnedIsoCodes
      );
      const possibleCount = countPossibleAnswers(template.id, subjectIso3, data);
      if (!isWithinConstraints(possibleCount, availableCount, constraints)) {
        continue;
      }

      if (availableCount > 0 && !candidateMap.has(questionKey)) {
        candidateMap.set(questionKey, {
          template,
          params,
          subjectIso3,
          availableCount,
          possibleCount,
          questionKey,
        });
      }
    }

    const candidates = Array.from(candidateMap.values());
    if (candidates.length === 0) {
      return null;
    }

    const minEap = getMinEap(data, history, usedIsoCodes, burnedIsoCodes);
    const allowEndgame = isEndgame(data, usedIsoCodes, burnedIsoCodes);
    const eligible = candidates.filter((candidate) => {
      if (!allowEndgame && candidate.availableCount <= 2) {
        return false;
      }
      if (!isWithinConstraints(candidate.possibleCount, candidate.availableCount, constraints)) {
        return false;
      }
      return candidate.availableCount >= minEap;
    });

    if (eligible.length === 0) {
      return null;
    }

    const weights = eligible.map((candidate) => candidate.availableCount - minEap + 1);
    const chosen = eligible[weightedRandomIndex(weights)];

    return {
      id: `q-${currentCount + 1}-${Date.now()}`,
      text: chosen.template.template(chosen.params),
      templateId: chosen.template.id,
      category: chosen.template.category,
      subjectIso3: chosen.subjectIso3,
      evidence: chosen.template.getEvidence?.(chosen.params, data) ?? null,
      possibleAnswerCount: chosen.possibleCount,
      isSingleAnswer: chosen.possibleCount === 1,
    };
  };

  return attemptGenerate(false) ?? attemptGenerate(true);
};

export const buildFallbackQuestion = (
  currentCount: number,
  constraints?: QuestionConstraints,
  usedIsoCodes: Set<string> = new Set(),
  burnedIsoCodes: Set<string> = new Set(),
  recentQuestionKeys?: Set<string>,
  filters?: QuestionFilter,
  history: HistoryItem[] = []
): QuestionInstance => {
  const data = getGameData();
  const maxAttempts = 200;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const { template, params } = getRandomTemplate(null, data, filters);
    const subjectIso3 = getSubjectIso3(params);
    const questionKey = getQuestionKey(template.id, subjectIso3);
    if (recentQuestionKeys?.has(questionKey)) {
      continue;
    }
    if (hasTemplateReachedLimit(template, history)) {
      continue;
    }
    const possibleCount = countPossibleAnswers(template.id, subjectIso3, data);
    const availableCount = countAvailableAnswers(
      template.id,
      subjectIso3,
      data,
      usedIsoCodes,
      burnedIsoCodes
    );
    if (!isWithinConstraints(possibleCount, availableCount, constraints)) {
      continue;
    }
    return {
      id: `q-${currentCount + 1}-${Date.now()}`,
      text: template.template(params),
      templateId: template.id,
      category: template.category,
      subjectIso3,
      evidence: template.getEvidence?.(params, data) ?? null,
      possibleAnswerCount: possibleCount,
      isSingleAnswer: possibleCount === 1,
    };
  }

  const { template, params } = getRandomTemplate(null, data, filters);
  const subjectIso3 = getSubjectIso3(params);
  const possibleCount = countPossibleAnswers(template.id, subjectIso3, data);
  return {
    id: `q-${currentCount + 1}-${Date.now()}`,
    text: template.template(params),
    templateId: template.id,
    category: template.category,
    subjectIso3,
    evidence: template.getEvidence?.(params, data) ?? null,
    possibleAnswerCount: possibleCount,
    isSingleAnswer: possibleCount === 1,
  };
};
